import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  ArtikalVanCenovnikaOpcija,
  CenovniciClient,
  FileResponse,
  GetNalepnicePdfQuery,
  GetNalepnicePreviewQuery,
  INalepniceRequest,
  IzostavljenArtikalDto,
  NalepnicaPolje,
  RobeClient,
  VrstaEntiteta,
} from '@kodit/core/data-api';
import { DynamicDialogConfig, DynamicDialogRef } from '@kodit/core/shared-ui';
import { AlertService } from '@kodit/core/services';
import { SelectItem } from 'primeng/api';
import { EMPTY, Subscription } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';

export interface StampaNalepnicaDialogData {
  /* ID-jevi izabranih artikala, redosledom kao u tabeli */
  artikliIds: number[];
}

export const NEMA_ARTIKALA_ZA_STAMPU =
  'Nema artikala za štampu u izabranom cenovniku.';

const DEFAULT_POLJA: NalepnicaPolje[] = [
  NalepnicaPolje.NAZIV,
  NalepnicaPolje.SIFRA,
  NalepnicaPolje.CENA,
];
const DEFAULT_BROJ_KOLONA = 3;

@Component({
  selector: 'kodit-stampa-nalepnica-dialog',
  templateUrl: './stampa-nalepnica-dialog.component.html',
  styleUrls: ['./stampa-nalepnica-dialog.component.scss'],
})
export class StampaNalepnicaDialogComponent implements OnInit, OnDestroy {
  /* Subs */
  private _subs: Subscription = new Subscription();

  /* Props */
  form!: FormGroup;
  artikliIds: number[] = [];
  cenovnici: SelectItem[] = [];
  isLoadingCenovnici = true;
  isGenerating = false;
  izostavljeni: IzostavljenArtikalDto[] = [];
  nijedanZaStampu = false;
  readonly nemaArtikalaZaStampu = NEMA_ARTIKALA_ZA_STAMPU;

  readonly vanCenovnikaOptions: SelectItem[] = [
    { label: 'Izostavi artikal', value: ArtikalVanCenovnikaOpcija.IZOSTAVI },
    {
      label: 'Uzmi cenu iz osnovnog cenovnika',
      value: ArtikalVanCenovnikaOpcija.OSNOVNI_CENOVNIK,
    },
  ];

  readonly poljaOptions: SelectItem[] = [
    { label: 'Naziv', value: NalepnicaPolje.NAZIV },
    { label: 'Šifra', value: NalepnicaPolje.SIFRA },
    { label: 'Cena (MP sa PDV-om)', value: NalepnicaPolje.CENA },
    { label: 'Jedinica mere', value: NalepnicaPolje.JEDINICA_MERE },
    { label: 'Jedinična cena (po kg)', value: NalepnicaPolje.JEDINICNA_CENA },
    { label: 'Barkod', value: NalepnicaPolje.BARKOD },
    { label: 'Datum štampe', value: NalepnicaPolje.DATUM_STAMPE },
  ];

  readonly kolonaOptions: SelectItem[] = [
    { label: '2 kolone', value: 2 },
    { label: '3 kolone', value: 3 },
    { label: '4 kolone', value: 4 },
  ];

  constructor(
    private _dialogRef: DynamicDialogRef,
    private _dialogConfig: DynamicDialogConfig,
    private _fb: FormBuilder,
    private _robeClient: RobeClient,
    private _cenovniciClient: CenovniciClient,
    private _alertService: AlertService
  ) {}

  ngOnInit(): void {
    const data = this._dialogConfig.data as StampaNalepnicaDialogData;
    this.artikliIds = data?.artikliIds ?? [];

    // podesavanja se ne pamte
    // ali stavicemo neko default ponasanje
    this.form = this._fb.group({
      cenovnikId: [null, Validators.required],
      artikalVanCenovnika: [ArtikalVanCenovnikaOpcija.IZOSTAVI],
      polja: [[...DEFAULT_POLJA]],
      brojKolona: [DEFAULT_BROJ_KOLONA],
    });

    this._loadCenovnici();
  }

  get poljaCtrl(): FormControl {
    return this.form.controls.polja as FormControl;
  }

  get artikliLabel(): string {
    return this._getArtikliLabel(this.artikliIds.length);
  }

  get isGenerisiDisabled(): boolean {
    return (
      this.isGenerating ||
      this.isLoadingCenovnici ||
      !this.artikliIds.length ||
      !this.poljaCtrl.value?.length ||
      this.form.invalid
    );
  }

  onGenerisi() {
    if (this.isGenerisiDisabled) {
      return;
    }

    const request: INalepniceRequest = {
      artikliIds: this.artikliIds,
      ...this.form.value,
    };

    this.isGenerating = true;
    this.izostavljeni = [];
    this.nijedanZaStampu = false;

    // prvo preview (lista izostavljenih), pa tek onda PDF
    // da korisnik zna sta je izostavljeno
    this._subs.add(
      this._robeClient
        .getNalepnicePreview(new GetNalepnicePreviewQuery(request))
        .pipe(
          switchMap((res) => {
            this.izostavljeni = res.data?.izostavljeni ?? [];

            if (!res.data?.brojZaStampu) {
              this.nijedanZaStampu = true;
              this._alertService.addWarnMsg(NEMA_ARTIKALA_ZA_STAMPU);
              return EMPTY;
            }

            return this._robeClient.getNalepnicePdf(
              new GetNalepnicePdfQuery(request)
            );
          }),
          finalize(() => (this.isGenerating = false))
        )
        .subscribe((file: FileResponse) => {
          this._otvoriPdf(file.data);

          if (!this.izostavljeni.length) {
            this._alertService.addSuccessMsg(
              'PDF sa nalepnicama je generisan.'
            );
            this._dialogRef.close(true);
            return;
          }

          // modal ostaje otvoren da bi korisnik video listu izostavljenih artikalaa
          this._alertService.addWarnMsg(
            `Izostavljeno: ${this.izostavljeni.length} ${this._getArtikliLabel(
              this.izostavljeni.length
            )}. Razlozi su navedeni u listi.`
          );
        })
    );
  }

  onOtkazi() {
    this._dialogRef.close();
  }

  private _loadCenovnici() {
    this.isLoadingCenovnici = true;
    this._subs.add(
      this._cenovniciClient
        .getAktivni()
        .pipe(finalize(() => (this.isLoadingCenovnici = false)))
        .subscribe((res) => {
          const cenovnici = res.data ?? [];
          this.cenovnici = cenovnici.map((x) => ({
            value: x.id,
            label: x.naziv,
            title: x.naziv,
          }));

          // default je osnovni cenovnik
          const osnovni =
            cenovnici.find(
              (x) => x.vrstaEntiteta === VrstaEntiteta.SISTEMSKI
            ) ?? cenovnici[0];
          this.form.controls.cenovnikId.setValue(osnovni?.id ?? null);
        })
    );
  }

  private _otvoriPdf(data: Blob) {
    const url = URL.createObjectURL(
      new Blob([data], { type: 'application/pdf' })
    );

    // ako browser blokira novi tab, PDF se preuzima
    if (!window.open(url, '_blank')) {
      const link = document.createElement('a');
      link.href = url;
      link.download = 'nalepnice_sa_cenama.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  private _getArtikliLabel(broj: number): string {
    const poslednjeDve = broj % 100;
    const poslednja = broj % 10;

    if (poslednjeDve >= 11 && poslednjeDve <= 14) {
      return 'artikala';
    }
    if (poslednja === 1) {
      return 'artikal';
    }
    if (poslednja >= 2 && poslednja <= 4) {
      return 'artikla';
    }
    return 'artikala';
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }
}
